import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ASSET_BASE = process.env.PUBLIC_URL || "";

interface MomentItem {
  id: number;
  image: string;
}

const data: MomentItem[] = [
  { id: 1, image: `${ASSET_BASE}/assets/moment1-v2.png` },
  { id: 2, image: `${ASSET_BASE}/assets/moment2-v2.png` },
  { id: 3, image: `${ASSET_BASE}/assets/moment3-v2.png` },
  { id: 4, image: `${ASSET_BASE}/assets/moment4-v2.png` },
  { id: 5, image: `${ASSET_BASE}/assets/moment5-v2.png` },
  { id: 6, image: `${ASSET_BASE}/assets/moment6-v2.png` },
  { id: 7, image: `${ASSET_BASE}/assets/moment7-v2.png` },
];

export default function MomentsCarousel() {
  const [activeIndex, setActiveIndex] = useState(1);
  const [open, setOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const next = useCallback(() => {
    setActiveIndex((i) => (i === data.length - 1 ? 0 : i + 1));
  }, []);

  const prev = () => setActiveIndex((i) => (i === 0 ? data.length - 1 : i - 1));

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (!open) {
      interval = setInterval(next, 3000);
    }
    return () => clearInterval(interval);
  }, [open, next]);

  // Added explicit number types to fix TS7006
  const handleOpen = (itemIndex: number) => {
    setSelectedIdx(itemIndex);
    setOpen(true);
  };

  const getItem = (offset: number) =>
    data[(activeIndex + offset + data.length) % data.length];

  const items = [getItem(-1), getItem(0), getItem(1)];

  return (
    <Box
      component="section"
      sx={{
        backgroundImage: `url(${ASSET_BASE}/assets/momentbg.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 6, md: 10 },
        bgcolor: "background.default",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: { xs: 4, md: 10 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Typography
          variant="h2"
          sx={{ color: "#a0116e", fontWeight: 700, textAlign: "left" }}
        >
          Stronger Together
        </Typography>
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-end" }}>
          <IconButton
            onClick={prev}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "2px solid #b7127e",
              bgcolor: "transparent",
              color: "#000",
              transition: "all 0.3s ease",
              mr: 2,
              "&:hover": {
                bgcolor: "#b7127e",
                borderColor: "#b7127e",
                color: "white",
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={next}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "2px solid #b7127e",
              bgcolor: "transparent",
              color: "#000",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#b7127e",
                borderColor: "#b7127e",
                color: "white",
              },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, md: 4 },
          minHeight: { xs: "300px", md: "420px" },
          px: { xs: 2, md: 4 },
        }}
      >
        {items.map((item, idx) => {
          const isCenter = idx === 1;
          const originalDataIndex = data.findIndex((d) => d.id === item.id);

          return (
            <Box
              key={item.id}
              onClick={() =>
                isCenter
                  ? handleOpen(originalDataIndex)
                  : setActiveIndex(originalDataIndex)
              }
              sx={{
                position: "relative",
                cursor: "pointer",
                transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                width: isCenter
                  ? { xs: "92vw", md: "clamp(380px, 38vw, 480px)" }
                  : { xs: 0, md: "clamp(260px, 27vw, 340px)" },
                height: isCenter
                  ? { xs: "280px", md: "clamp(300px, 30vw, 380px)" }
                  : { xs: 0, md: "clamp(200px, 21vw, 260px)" },
                display: { xs: isCenter ? "block" : "none", md: "block" },
                filter: isCenter ? "none" : "grayscale(100%)",
                transform: isCenter ? "scale(1.05)" : "scale(0.9)",
                zIndex: isCenter ? 2 : 1,
                borderRadius: { xs: "16px", md: "24px" },
                overflow: "hidden",
                bgcolor: "#000",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={item.image}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              bgcolor: "rgba(0, 0, 0, 0.85)",
              backdropFilter: "blur(12px)",
            },
          },
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", md: "80vw" },
              maxWidth: "800px",
              outline: "none",
              zIndex: 1400,
            }}
          >
            {/* ── Close Button Fixed ── */}
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: "absolute",
                // Moved slightly inside the view to ensure laptop visibility
                top: { xs: -50, md: -10 },
                right: { xs: 0, md: -10 },
                color: "common.white",
                bgcolor: "rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "#b7127e" },
                zIndex: 1500, // Ensure it's above the image
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Navigation Left */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIdx((i) => (i === 0 ? data.length - 1 : i - 1));
              }}
              sx={{
                position: "absolute",
                left: { xs: 0, md: -80 },
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
                "&:hover": { color: "#b7127e" },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <ArrowBackIosNewIcon
                sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
              />
            </IconButton>

            {/* Image Container */}
            <Box
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0px 10px 40px rgba(0,0,0,0.6)",
                bgcolor: "black",
                lineHeight: 0,
              }}
            >
              <Box
                component="img"
                src={data[selectedIdx]?.image}
                sx={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>

            {/* Navigation Right */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIdx((i) => (i === data.length - 1 ? 0 : i + 1));
              }}
              sx={{
                position: "absolute",
                right: { xs: 0, md: -80 },
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
                "&:hover": { color: "#b7127e" },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <ArrowForwardIosIcon
                sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
              />
            </IconButton>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
